import dayjs from 'dayjs'

import {
    CAPACITY_API_STATUS,
    CAPACITY_DAILY_HOURS,
    CAPACITY_OCCUPIED_THRESHOLD,
    CAPACITY_PARTIAL_THRESHOLD,
    CAPACITY_STATUS,
    CAPACITY_STATUS_COLORS,
    CAPACITY_STATUS_LABELS,
} from './capacity.constants'

/** Normalize picker / stored values to millisecond timestamps for API payload. */
export const toCapacityApiDate = (value, boundary = 'start') => {
    if (value == null || value === '') return null
    const date =
        typeof value === 'number' && Number.isFinite(value)
            ? dayjs(value)
            : dayjs(value)
    if (!date.isValid()) return null
    return boundary === 'end'
        ? date.endOf('day').valueOf()
        : date.startOf('day').valueOf()
}

export const toCapacityApiStatus = (status) =>
    CAPACITY_API_STATUS[status] || CAPACITY_API_STATUS[CAPACITY_STATUS.ALL]

export const buildCapacityDashboardPayload = ({
    start,
    end,
    projectIds = [],
    userIds = [],
    statusFilter,
    search = '',
} = {}) => ({
    startDate: toCapacityApiDate(start, 'start'),
    endDate: toCapacityApiDate(end, 'end'),
    projectId: Array.isArray(projectIds) ? projectIds : [],
    userId: Array.isArray(userIds) ? userIds : [],
    status: toCapacityApiStatus(statusFilter),
    search: String(search || '').trim(),
})

export const normalizeCapacityDashboardResult = (result = {}) => {
    const users = Array.isArray(result.users) ? result.users : []

    return {
        users,
        summary: {
            totalAssignedHours: Number(result.totalAssignedHours) || 0,
            availableHours: Number(result.availableHours) || 0,
            occupiedPercent: Number(result.occupied) || 0,
            activeProjects: Number(result.activeProjects) || 0,
            freeFrom: result.estimatedFreeDate || null,
        },
    }
}

export const getEmptyCapacitySummary = () => ({
    totalAssignedHours: 0,
    availableHours: 0,
    occupiedPercent: 0,
    activeProjects: 0,
    freeFrom: null,
})

export const getAvailabilityStatus = (percent) => {
    if (percent >= CAPACITY_OCCUPIED_THRESHOLD) return CAPACITY_STATUS.OCCUPIED
    if (percent >= CAPACITY_PARTIAL_THRESHOLD) return CAPACITY_STATUS.PARTIAL
    return CAPACITY_STATUS.AVAILABLE
}

export const getStatusLabel = (status) =>
    CAPACITY_STATUS_LABELS[status] || status

export const getStatusColor = (status) =>
    CAPACITY_STATUS_COLORS[status] || '#94a3b8'

export const formatCapacityHours = (hours) => {
    const value = Number(hours) || 0
    return `${Math.round(value)} Hr`
}

export const formatCapacityDate = (date) => {
    if (!date) return '—'
    return dayjs(date).format('DD MMM')
}

export const formatCapacityDateFull = (date) => {
    if (!date) return '—'
    return dayjs(date).format('DD MMM YYYY')
}

export const getDefaultCapacityRange = () => {
    const start = dayjs().startOf('month')
    const end = dayjs().add(1, 'month').endOf('month')
    return {
        start: start.valueOf(),
        end: end.valueOf(),
    }
}

export const enumerateDays = (rangeStart, rangeEnd) => {
    const days = []
    let current = dayjs(rangeStart).startOf('day')
    const end = dayjs(rangeEnd).startOf('day')
    if (!current.isValid() || !end.isValid() || current.isAfter(end)) return days

    while (current.isBefore(end) || current.isSame(end, 'day')) {
        days.push({
            date: current.format('YYYY-MM-DD'),
            dayjs: current,
            label: current.format('DD'),
        })
        current = current.add(1, 'day')
    }
    return days
}

export const getTaskDailyAllocations = (task, projectName, projectId) => {
    const start = dayjs(task.startDate).startOf('day')
    const end = dayjs(task.endDate).startOf('day')
    if (!start.isValid() || !end.isValid()) return []

    const dayCount = Math.max(end.diff(start, 'day') + 1, 1)
    const hoursPerDay = (Number(task.hours) || 0) / dayCount
    const allocations = []
    let current = start

    while (current.isBefore(end) || current.isSame(end, 'day')) {
        allocations.push({
            date: current.format('YYYY-MM-DD'),
            hours: hoursPerDay,
            taskName: task.taskName,
            projectName,
            projectId,
            startDate: task.startDate,
            endDate: task.endDate,
            totalHours: task.hours,
        })
        current = current.add(1, 'day')
    }
    return allocations
}

export const buildDailyOccupancy = (user, rangeStart, rangeEnd) => {
    const days = enumerateDays(rangeStart, rangeEnd).map((d) => ({
        ...d,
        hours: 0,
        segments: [],
    }))

    const dayMap = Object.fromEntries(days.map((d) => [d.date, d]))

    ;(user?.projects || []).forEach((project) => {
        ;(project?.tasks || []).forEach((task) => {
            getTaskDailyAllocations(
                task,
                project.projectName,
                project.projectId
            ).forEach((alloc) => {
                const bucket = dayMap[alloc.date]
                if (!bucket) return
                bucket.hours += alloc.hours
                bucket.segments.push(alloc)
            })
        })
    })

    return days.map((d) => {
        const percent = Math.round(
            (d.hours / CAPACITY_DAILY_HOURS) * 100
        )
        return {
            ...d,
            percent,
            status: getAvailabilityStatus(percent),
        }
    })
}

export const buildMonthGroups = (days) => {
    const groups = []
    days.forEach((day) => {
        const key = day.dayjs.format('YYYY-MM')
        const label = day.dayjs.format('MMMM')
        const last = groups[groups.length - 1]
        if (last?.key === key) {
            last.span += 1
        } else {
            groups.push({ key, label, span: 1 })
        }
    })
    return groups
}

export const getUserOccupiedPercent = (user, rangeStart, rangeEnd) => {
    const daily = buildDailyOccupancy(user, rangeStart, rangeEnd)
    if (!daily.length) return 0
    const totalCapacity = daily.length * CAPACITY_DAILY_HOURS
    const totalAssigned = daily.reduce((sum, d) => sum + d.hours, 0)
    return Math.round((totalAssigned / totalCapacity) * 100)
}

export const getUserStatus = (user, rangeStart, rangeEnd) =>
    getAvailabilityStatus(getUserOccupiedPercent(user, rangeStart, rangeEnd))

export const getProjectHoursInRange = (project, rangeStart, rangeEnd) => {
    const rangeStartDay = dayjs(rangeStart).startOf('day')
    const rangeEndDay = dayjs(rangeEnd).startOf('day')

    return (project?.tasks || []).reduce((sum, task) => {
        const taskStart = dayjs(task.startDate).startOf('day')
        const taskEnd = dayjs(task.endDate).startOf('day')
        if (taskEnd.isBefore(rangeStartDay) || taskStart.isAfter(rangeEndDay)) {
            return sum
        }
        return sum + (Number(task.hours) || 0)
    }, 0)
}

export const getActiveProjectCount = (user, rangeStart, rangeEnd) =>
    (user?.projects || []).filter(
        (p) => getProjectHoursInRange(p, rangeStart, rangeEnd) > 0
    ).length

export const computeUserSummary = (user, rangeStart, rangeEnd) => {
    const daily = buildDailyOccupancy(user, rangeStart, rangeEnd)
    const totalCapacity = daily.length * CAPACITY_DAILY_HOURS
    const totalAssigned = daily.reduce((sum, d) => sum + d.hours, 0)
    const availableHours = Math.max(totalCapacity - totalAssigned, 0)
    const occupiedPercent = totalCapacity
        ? Math.round((totalAssigned / totalCapacity) * 100)
        : 0

    return {
        totalAssignedHours: Math.round(totalAssigned),
        availableHours: Math.round(availableHours),
        occupiedPercent,
        activeProjects: getActiveProjectCount(user, rangeStart, rangeEnd),
        freeFrom: user?.availableFrom || null,
        status: getAvailabilityStatus(occupiedPercent),
    }
}

export const buildProjectTimelineRows = (user, rangeStart, rangeEnd) =>
    (user?.projects || [])
        .map((project) => ({
            projectId: project.projectId,
            projectName: project.projectName,
            hours: Math.round(getProjectHoursInRange(project, rangeStart, rangeEnd)),
            tasks: project.tasks || [],
        }))
        .filter((p) => p.hours > 0)

export const computeAggregateSummary = (users, rangeStart, rangeEnd) => {
    if (!users?.length) {
        return {
            totalAssignedHours: 0,
            availableHours: 0,
            occupiedPercent: 0,
            activeProjects: 0,
            freeFrom: null,
            status: CAPACITY_STATUS.AVAILABLE,
        }
    }

    const summaries = users.map((user) =>
        computeUserSummary(user, rangeStart, rangeEnd)
    )
    const totalAssignedHours = summaries.reduce(
        (sum, item) => sum + item.totalAssignedHours,
        0
    )
    const availableHours = summaries.reduce(
        (sum, item) => sum + item.availableHours,
        0
    )
    const totalCapacity = totalAssignedHours + availableHours
    const occupiedPercent = totalCapacity
        ? Math.round((totalAssignedHours / totalCapacity) * 100)
        : 0

    const projectIds = new Set()
    users.forEach((user) => {
        buildProjectTimelineRows(user, rangeStart, rangeEnd).forEach((p) => {
            projectIds.add(p.projectId || p.projectName)
        })
    })

    const freeFrom = users
        .map((u) => u.availableFrom)
        .filter(Boolean)
        .sort((a, b) => dayjs(a).valueOf() - dayjs(b).valueOf())
        .pop()

    return {
        totalAssignedHours,
        availableHours,
        occupiedPercent,
        activeProjects: projectIds.size,
        freeFrom: freeFrom || null,
        status: getAvailabilityStatus(occupiedPercent),
    }
}

export const buildTableRows = (users, rangeStart, rangeEnd) => {
    const rows = []
    ;(users || []).forEach((user) => {
        const userPercent = getUserOccupiedPercent(user, rangeStart, rangeEnd)
        const userStatus = getAvailabilityStatus(userPercent)

        ;(user?.projects || []).forEach((project) => {
            const assignedHours = getProjectHoursInRange(
                project,
                rangeStart,
                rangeEnd
            )
            if (assignedHours <= 0) return

            rows.push({
                key: `${user.userId}-${project.projectId || project.projectName}`,
                userId: user.userId,
                userName: user.userName,
                projectName: project.projectName,
                taskCount:
                    project.taskCount ?? (project.tasks || []).length,
                assignedHours: Math.round(
                    project.totalAssignedHours ?? assignedHours
                ),
                availability: userStatus,
                availabilityLabel: getStatusLabel(userStatus),
                freeDate: user.availableFrom,
            })
        })
    })
    return rows
}

export const extractUserOptions = (users) =>
    (users || []).map((u) => ({
        value: u.userId,
        label: u.userName,
    }))

export const filterCapacityUsers = (
    users,
    { search, projectIds, status, rangeStart, rangeEnd }
) => {
    let result = [...(users || [])]

    const query = String(search || '')
        .trim()
        .toLowerCase()
    if (query) {
        result = result.filter((u) =>
            String(u.userName || '')
                .toLowerCase()
                .includes(query)
        )
    }

    if (projectIds?.length) {
        result = result
            .map((user) => ({
                ...user,
                projects: (user.projects || []).filter((p) =>
                    projectIds.includes(p.projectId || p.projectName)
                ),
            }))
            .filter((user) => user.projects.length > 0)
    }

    if (status && status !== CAPACITY_STATUS.ALL) {
        result = result.filter(
            (u) => getUserStatus(u, rangeStart, rangeEnd) === status
        )
    }

    return result
}

export const getTimelineSegmentStyle = (task, rangeStart, rangeEnd, dayCount) => {
    const rangeStartDay = dayjs(rangeStart).startOf('day')
    const rangeEndDay = dayjs(rangeEnd).startOf('day')
    const totalDays = Math.max(rangeEndDay.diff(rangeStartDay, 'day') + 1, 1)

    const taskStart = dayjs(task.startDate).startOf('day')
    const taskEnd = dayjs(task.endDate).startOf('day')

    const visibleStart = taskStart.isBefore(rangeStartDay)
        ? rangeStartDay
        : taskStart
    const visibleEnd = taskEnd.isAfter(rangeEndDay) ? rangeEndDay : taskEnd

    if (visibleEnd.isBefore(rangeStartDay) || visibleStart.isAfter(rangeEndDay)) {
        return null
    }

    const leftDays = visibleStart.diff(rangeStartDay, 'day')
    const spanDays = visibleEnd.diff(visibleStart, 'day') + 1

    return {
        leftPercent: (leftDays / totalDays) * 100,
        widthPercent: (spanDays / totalDays) * 100,
        dayCount,
    }
}
